if(!customElements.get('youtube-video')) {
    class YouTubeVideo extends HTMLElement {
      constructor() {
        super();
        this.thumbnail = this.querySelector('.youtube-thumbnail');
        this.iframeContainer = this.querySelector('.youtube-iframe-container');
        this.videoId = this.getAttribute('data-video-id');
        this.autoplay = this.hasAttribute('data-autoplay');
        this.dynamicSize = this.hasAttribute('data-dynamic-size');
        this.hideControls = this.hasAttribute('data-hide-controls');
        this.calculateHeight = this.hasAttribute('data-calculate-height');
        this.useResizeObserver = this.hasAttribute('data-use-resize-observer');
        this.sizeDefaults = {
          desktopWidth: 450,
          desktopHeight: 253,
          tabletWidth: 350,
          tabletHeight: 197,
          mobileWidth: 280,
          mobileHeight: 158
        }
        
        // Get and set initial dimensions
        if(!this.dynamicSize) this.setStaticSize();
        
        this.allInstances = document.querySelectorAll(
          `youtube-video[data-video-id="${this.videoId}"]`
        );
  
        this.directLoad = this.iframeContainer.querySelector('iframe') !== null;
        if (this.directLoad) {
          const iframe = this.iframeContainer.querySelector('iframe');
          if (window.YT && window.YT.Player) {
            this.setupExistingPlayer(iframe);
          } else {
            window.addEventListener('youtube:api:ready', () => this.setupExistingPlayer(iframe), { once: true });
          }
        }
      }
  
      getVideoDimensions() {
        if(this.dynamicSize){
          const rect = this.getBoundingClientRect();
          const width = this.calculateWidth ? rect.width * 16 / 9 : rect.width ;
          const height = this.calculateHeight ? rect.width * 9 / 16 : rect.height;
          return { width: `${width}px`, height: `${height}px`, numericWidth: width, numericHeight: height };
        }
        const styles = getComputedStyle(this);
        const isMobile = window.innerWidth <= 768;
        const isTablet = window.innerWidth > 768 && window.innerWidth <= 1024;
        
        let width, height;
         
        if (isMobile) {
          width = styles.getPropertyValue('--video-width-mobile').trim() || `${this.sizeDefaults.mobileWidth}px`;
          height = styles.getPropertyValue('--video-height-mobile').trim() || `${this.sizeDefaults.mobileHeight}px`;
        } else if (isTablet) {
          width = styles.getPropertyValue('--video-width-tablet').trim() || `${this.sizeDefaults.tabletWidth}px`;
          height = styles.getPropertyValue('--video-height-tablet').trim() || `${this.sizeDefaults.tabletHeight}px`;
        } else {
          width = styles.getPropertyValue('--video-width').trim() || `${this.sizeDefaults.desktopWidth}px`;
          height = styles.getPropertyValue('--video-height').trim() || `${this.sizeDefaults.desktopHeight}px`;
        }
        return { width: `${width}px`, height: `${height}px`, numericWidth: parseInt(width), numericHeight: parseInt(height) };
      }
  
  
      setupExistingPlayer(iframe) {
        const { numericWidth, numericHeight } = this.getVideoDimensions();
        
        const playerVars = {
          autoplay: this.autoplay ? 1 : 0,
          mute: 1,
          playsinline: 1
        };
        
        if (this.hideControls) {
          playerVars.controls = 0; 
          playerVars.rel = 0; 
        }
        
        this.player = new YT.Player(iframe, {
          width: numericWidth,
          height: numericHeight,
          playerVars: playerVars,
          events: {
            onStateChange: (e) => this.onPlayerStateChange(e)
          }
        });
      }
  
      connectedCallback() {
        // Only add event listeners if we're not in direct load mode
        if (!this.directLoad) {
          this.addEventListener('click', (e) => this.handleInteraction(e));
        }
        if(this.dynamicSize) this.setDynamicSize();
        if(this.useResizeObserver) this.createResizeObserver();
      }
  
      onPlayerStateChange(event) {
        const state = event.data;
        // Dispatch state change event for parent components to handle
        this.dispatchEvent(new CustomEvent('youtube:state:change', {
          bubbles: true,
          detail: {
            state,
            videoElement: this
          }
        }));
      }
  
      handleInteraction(event) {
        if (!this.directLoad) {
          // Load all unloaded instances of this video
          this.allInstances.forEach(instance => {
            if (instance.directLoad) return;
            if(instance.classList.contains('desktop') && window.innerWidth < 989){
              return;
            }
            if(instance.classList.contains('mobile') && window.innerWidth > 989){
              return;
            }
            instance.classList.add('loaded');
            instance.directLoad = true;
            
            const playerDiv = document.createElement('div');
            instance.iframeContainer.appendChild(playerDiv);
            const isTargetInstance = event.target == instance.querySelector('.youtube-iframe-container'); 
            const createPlayer = () => {
              const { numericWidth, numericHeight } = instance.getVideoDimensions();
              const playerVars = {
                autoplay: instance.autoplay ? 1 : 0,
                mute: 1,
                playsinline: 1
              };
              
              if (instance.hideControls) {
                playerVars.controls = 0;
                playerVars.rel = 0;      
              }
              
              instance.player = new YT.Player(playerDiv, {
                videoId: instance.videoId,
                width: numericWidth,
                height: numericHeight,
                playerVars: playerVars,
                events: {
                  onStateChange: (e) => instance.onPlayerStateChange(e),
                  onReady: (e) => {
                    if(isTargetInstance){
                      // Mute the video for autoplay compliance
                      e.target.mute();
                      e.target.playVideo();
                      
                      // For mobile devices, request fullscreen
                      if(window.innerWidth <= 768) {
                        setTimeout(() => {
                          const iframe = e.target.getIframe();
                          if(iframe && iframe.requestFullscreen) {
                            iframe.requestFullscreen().catch(err => {
                              console.error('Fullscreen request failed:', err);
                            });
                          } else if(iframe && iframe.webkitRequestFullscreen) {
                            iframe.webkitRequestFullscreen();
                          } else if(iframe && iframe.mozRequestFullScreen) {
                            iframe.mozRequestFullScreen();
                          }
                        }, 500);
                      }
                    }
                  },
                  onError: (event) => {
                    instance.getErrorMessage(event.data);
                  }
  
                },
                playerVars: {
                  enablejsapi: 1,
                  origin: window.location.origin,
                  playsinline: window.innerWidth > 768 ? 1 : 0, // Full screen on mobile
                  rel: 0,
                  modestbranding: 1,
                  autoplay: isTargetInstance ? 1 : 0,
                  mute: isTargetInstance ? 1 : 0 // Mute on autoplay
                }
              });
            };
  
            // Fade out thumbnail
            instance.thumbnail.style.opacity = '0';
            setTimeout(() => {
              instance.thumbnail.style.display = 'none';
            }, 1000);
  
            // Initialize player if API is ready, otherwise wait for it
            if (window.YT && window.YT.Player) {
              createPlayer();
            } else {
              window.addEventListener('youtube:api:ready', () => createPlayer(), { once: true });
            }
          });
        }
      }
  
      getStateName(state) {
        switch(state) {
          case -1: return 'unstarted';
          case 0: return 'ended';
          case 1: return 'playing';
          case 2: return 'paused';
          case 3: return 'buffering';
          case 5: return 'video cued';
          default: return 'unknown';
        }
      }
  
      setDynamicSize(){
        const dimensions = this.getVideoDimensions(); 
        if(this.calculateHeight) this.style.height = dimensions.height;
        if(this.calculateWidth) this.style.width = dimensions.width;
        if(this.player) {
          const { numericWidth, numericHeight } = dimensions;
          this.player.setSize(numericWidth, numericHeight);
        }
      }
  
      setStaticSize(){
        const dimensions = this.getVideoDimensions();
        this.iframeContainer.style.width = dimensions.width;
        this.iframeContainer.style.height = dimensions.height;
        this.iframeContainer.style.minWidth = '200px';
        this.iframeContainer.style.minHeight = '112.5px';
      }
  
      createResizeObserver(){
        this.resizeObserver = new ResizeObserver(() => {
          if(this.dynamicSize) this.setDynamicSize();
          else this.setStaticSize();
        });
        this.resizeObserver.observe(this);
      }
  
      getErrorMessage(errorCode) {
        switch(errorCode) {
          case 2:
            return 'Invalid parameter value';
          case 5:
            return 'HTML5 player error';
          case 100:
            return 'Video not found or removed';
          case 101:
          case 150:
            return 'Video owner does not allow embedding';
          default:
            return 'Unknown error';
        }
      }
  
      disconnectedCallback() {
        if (!this.directLoad) {
          this.removeEventListener('click', (e) => this.handleInteraction(e));
        }
        if (this.player && this.player.destroy) {
          this.player.destroy();
        }
        if (this.resizeObserver) {
          this.resizeObserver.disconnect();
        }
      }
    }
  
    customElements.define('youtube-video', YouTubeVideo); 
  }